import { useState, useEffect, useCallback } from 'react';
import { useOfflineDetection } from './useOfflineDetection';
import * as Sentry from "@sentry/react";

interface QueuedOperation {
  id: string;
  type: 'assessment' | 'preferences' | 'generic';
  operationType: string;
  operationPayload: any;
  data: any;
  description: string;
  priority: 'high' | 'medium' | 'low';
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

const STORAGE_KEY = 'offline_queue';
const MAX_RETRY_ATTEMPTS = 3;
const MAX_QUEUE_SIZE = 100;
const MAX_QUEUE_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

// Operation handlers - reconstruct operations from stored type and payload
const OPERATION_HANDLERS: Record<string, (payload: any, context?: any) => Promise<any>> = {
  'profile_update': async (payload) => {
    const { supabase } = await import('@/integrations/supabase/client');
    const { data, error } = await supabase
      .from('profiles')
      .update(payload.updates)
      .eq('user_id', payload.userId)
      .select()
      .single();
    if (error) throw error;
    return data;
  },
  'profile_image_upload': async (payload) => {
    const { supabase } = await import('@/integrations/supabase/client');
    const { file, userId } = payload;
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/profile.${fileExt}`;
    
    const { error: uploadError } = await supabase.storage
      .from('profile-images')
      .upload(fileName, file, { upsert: true });
    
    if (uploadError) throw uploadError;
    
    const { data: { publicUrl } } = supabase.storage
      .from('profile-images')
      .getPublicUrl(fileName);
    
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ profile_image: publicUrl })
      .eq('user_id', userId)
      .select()
      .single();
    
    if (updateError) throw updateError;
  },
  'preferences_update': async (payload) => {
    const { supabase } = await import('@/integrations/supabase/client');
    const { userId, updates } = payload;
    const { error } = await supabase.rpc('upsert_user_preferences', {
      p_user_id: userId,
      p_theme: updates.theme || null,
      p_email_notifications: updates.email_notifications ?? null,
      p_push_notifications: updates.push_notifications ?? null,
      p_preferences: updates.preferences || null,
    });
    if (error) throw error;
  },
  'assessment_update': async (payload) => {
    const { supabase } = await import('@/integrations/supabase/client');
    const { userId, moduleId, progress } = payload;
    const { error } = await supabase.rpc('upsert_assessment_progress', {
      p_user_id: userId,
      p_module_id: moduleId,
      p_current_question_index: progress.questionIndex,
      p_answers: progress.answers,
      p_total_questions: Object.keys(progress.answers).length || 20
    });
    if (error) throw error;
  },
};

// Reconstruct operation from stored type and payload
function reconstructOperation(operationType: string, payload: any): () => Promise<any> {
  const handler = OPERATION_HANDLERS[operationType];
  if (!handler) {
    throw new Error(`Unknown operation type: ${operationType}`);
  }
  return () => handler(payload);
}

export function useOfflineQueue() {
  const { isOnline, queueOperation: queueOfflineOperation } = useOfflineDetection();
  const [queue, setQueue] = useState<QueuedOperation[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  // Load queue from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedQueue = JSON.parse(stored);
        // Filter out old items on load
        const now = Date.now();
        const filteredQueue = parsedQueue.filter((item: QueuedOperation) => 
          (now - item.timestamp) < MAX_QUEUE_AGE
        );
        setQueue(filteredQueue);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'storage_error', operation: 'offline_queue', action: 'load' },
        extra: { message: 'Failed to load offline queue' }
      });
    }
  }, []);

  // Save queue to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'storage_error', operation: 'offline_queue', action: 'save' },
        extra: { message: 'Failed to save offline queue', queueLength: queue.length }
      });
    }
  }, [queue]);

  // Process queue when coming back online
  useEffect(() => {
    if (isOnline && queue.length > 0 && !isProcessing) {
      processQueue();
    }
  }, [isOnline, queue.length, isProcessing]);

  const addToQueue = useCallback((
    type: QueuedOperation['type'],
    operationType: string,
    operationPayload: any,
    data: any,
    description?: string,
    priority: QueuedOperation['priority'] = 'medium'
  ): string => {
    const queuedOp: QueuedOperation = {
      id: crypto.randomUUID(),
      type,
      operationType,
      operationPayload,
      data,
      description: description || operationType,
      priority,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: MAX_RETRY_ATTEMPTS
    };

    setQueue(prev => {
      let updated = [...prev];
      
      // Step 1: Add new operation first
      updated.push(queuedOp);
      
      // Step 2: Sort by priority (high > medium > low) and timestamp
      updated = updated.sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        return priorityDiff !== 0 ? priorityDiff : a.timestamp - b.timestamp;
      });
      
      // Step 3: Remove LOWEST priority item if queue exceeds max size
      // After sort, lowest priority items are at the end
      if (updated.length > MAX_QUEUE_SIZE) {
        updated.pop();
      }
      
      return updated;
    });

    // If online, try to process immediately
    if (isOnline) {
      setTimeout(() => processQueue(), 100);
    }

    return queuedOp.id;
  }, [isOnline]);

  const processQueue = useCallback(async () => {
    if (isProcessing || queue.length === 0) return;

    setIsProcessing(true);
    const processedIds: string[] = [];
    const failedOps: QueuedOperation[] = [];
    const failedOperations: string[] = [];

    for (const op of queue) {
      try {
        // Reconstruct operation from stored type and payload
        const operation = reconstructOperation(op.operationType, op.operationPayload);
        await operation();
        
        processedIds.push(op.id);
      } catch (error) {
        Sentry.captureException(error, {
          tags: { context: 'storage_error', operation: 'offline_queue', action: 'process' },
          extra: { message: `Failed to process queued operation: ${op.description}` }
        });
        
        // Increment retry count
        const updatedOp = { ...op, retryCount: op.retryCount + 1 };
        
        if (updatedOp.retryCount < updatedOp.maxRetries) {
          failedOps.push(updatedOp);
        } else {
          failedOperations.push(op.description);
          
          Sentry.captureException(error, {
            tags: { context: 'storage_error', operation: 'offline_queue', action: 'max_retries' },
            extra: { message: `Max retries exceeded for operation: ${op.description}`, retries: updatedOp.maxRetries }
          });
          
          processedIds.push(op.id);
        }
      }
    }

    // Update queue: remove processed operations, update failed ones

    // Update queue: remove processed operations, update failed ones
    setQueue(prev => {
      const remaining = prev.filter(op => !processedIds.includes(op.id));
      const withRetries = remaining.map(op => {
        const failed = failedOps.find(f => f.id === op.id);
        return failed || op;
      });
      return withRetries;
    });

    setIsProcessing(false);
  }, [queue, isProcessing]);

  const removeFromQueue = useCallback((id: string) => {
    setQueue(prev => prev.filter(op => op.id !== id));
  }, []);

  const cleanupOldItems = useCallback(() => {
    const now = Date.now();
    const beforeCount = queue.length;
    
    setQueue(prev => {
      const cleaned = prev.filter((item: QueuedOperation) => 
        (now - item.timestamp) < MAX_QUEUE_AGE
      );
      
      return cleaned;
    });
  }, [queue.length]);

  const clearQueue = useCallback(() => {
    setQueue([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const getQueueSummary = useCallback(() => {
    const summary = {
      total: queue.length,
      byType: queue.reduce((acc, op) => {
        acc[op.type] = (acc[op.type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      byPriority: queue.reduce((acc, op) => {
        acc[op.priority] = (acc[op.priority] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      oldestTimestamp: queue.length > 0 ? Math.min(...queue.map(op => op.timestamp)) : null
    };
    return summary;
  }, [queue]);

  // Periodic cleanup of old items (every 6 hours)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      cleanupOldItems();
    }, 6 * 60 * 60 * 1000);  // 6 hours in milliseconds
    
    return () => clearInterval(cleanupInterval);
  }, [cleanupOldItems]);

  return {
    queue,
    addToQueue,
    processQueue,
    removeFromQueue,
    clearQueue,
    getQueueSummary,
    isProcessing,
    queueLength: queue.length
  };
}

// Specialized hooks for different operation types
export function useAssessmentOfflineQueue() {
  const { addToQueue, ...rest } = useOfflineQueue();

  const queueAssessmentSave = useCallback((
    userId: string,
    moduleId: string,
    progress: any
  ) => {
    return addToQueue(
      'assessment',
      'assessment_update',
      { userId, moduleId, progress },
      { moduleId, progress },
      `Save assessment progress for ${moduleId}`,
      'high'
    );
  }, [addToQueue]);

  return {
    queueAssessmentSave,
    ...rest
  };
}

export function usePreferencesOfflineQueue() {
  const { addToQueue, ...rest } = useOfflineQueue();

  const queuePreferencesSave = useCallback((
    userId: string,
    updates: any
  ) => {
    return addToQueue(
      'preferences',
      'preferences_update',
      { userId, updates },
      { preferences: updates },
      'Save user preferences',
      'medium'
    );
  }, [addToQueue]);

  return {
    queuePreferencesSave,
    ...rest
  };
}